import { useEffect, useMemo, useRef, useState } from 'react';

import { SearchSettings } from '@givebacks/api/query';
import { RowData, SortingState, useReactTable } from '@tanstack/react-table';
import { createStore, useSetAtom } from 'jotai';
import { DatesRangeValue } from 'ui';
import { useId } from 'ui/Hooks';

import { SearchProperties } from '../types/utils';
import { formatDateRange } from './formatters';
import { serializeDateRange, serializeMultiSelect, serializeString } from './serializers';
import { tableSearchAtomFamily, tableSearchListAtom } from './store';
import { TableSearchObject } from './types';

type store = ReturnType<typeof createStore>;

type Serializers = 'multi-select' | 'string' | 'date-range';

/** Format of the search data */
type TSearchFormats = string[] | string | DatesRangeValue | undefined | null;

const SERIALIZERS_DICT = {
  'multi-select': serializeMultiSelect,
  'date-range': serializeDateRange,
  string: serializeString,
} as const;

/** Returns the specified serializer  */
function getSerializer<TFormat extends TSearchFormats>(serializer: Serializers) {
  return SERIALIZERS_DICT[serializer] as (
    value: TFormat,
    searchBy?: string
  ) => { value: string; by?: string };
}
/**
 * Formats the search value based on the serializer
 */
const FORMATTER_DICT = {
  'date-range': formatDateRange,
  'multi-select': (value: string[] | string) => (Array.isArray(value) ? value.join(', ') : value),
  string: (value: string) => value,
} as const;

const getFormatter = <TFormat extends TSearchFormats>(serializer?: Serializers) => {
  if (!serializer) return FORMATTER_DICT.string;
  return FORMATTER_DICT[serializer] as (value: TFormat) => string;
};

type SerializedSearchFormat<TSerializer extends Serializers> = TSerializer extends 'multi-select'
  ? string[]
  : TSerializer extends 'date-range'
    ? DatesRangeValue
    : string;

/**
 * Provides a search object to the table,
 */

const useTableSearch = <
  TFields extends SearchSettings['searches'],
  TSerializerFormat extends Serializers,
  TSearchFormat extends SerializedSearchFormat<TSerializerFormat> | null | undefined,
>({
  search,
  store,
  reset,
  fields,
  serializer,
  searchBy,
  label,
  customFormatter,
  showInParams = true,
}: {
  /** Atom Store */
  store: store;
  /** Callback Called when the Reset atom is used */
  reset?: () => void;
  /** Search values to be serialized and set in the atom */
  search?: TSearchFormat;
  /**
   *  How the search should be serialized, sets by search type
   *  Serializing is done so that we can serialize the search object into a format that can be used by the API
   */
  serializer?: TSerializerFormat;
  /** Fields that the serialized search object will be created from the search  */
  fields?: SearchProperties<TFields>;
  searchBy?: string;
  /** The Human Readable label, corresponds to the search input */
  label?: string;
  /** Optional formatter for when formatting needs to be handled with a different scope */
  customFormatter?: (value: string) => string;
  /** Whether the search should be shown in the url params */
  showInParams?: boolean;
}) => {
  const fieldsRef = useRef(fields);
  const serializerRef = useRef(serializer);

  const [searchObject, setSearchObject] = useState<TableSearchObject>();

  const uuid = useId();

  const setSearchAtomOnList = useSetAtom(tableSearchListAtom, { store });

  // Return unique atom from map using our uuid
  const [stableAtomFromMap, setStableAtomFromMap] = useState(() =>
    tableSearchAtomFamily({ id: uuid })
  );

  stableAtomFromMap.debugLabel = `stableAtomFromMap-${uuid}`;

  /** Update the current pointer atom */
  const setAtomValue = useSetAtom(stableAtomFromMap, { store });

  useEffect(() => {
    setStableAtomFromMap(tableSearchAtomFamily({ id: uuid }));
    setSearchAtomOnList((list) => [...list, uuid]);

    return () => {
      // Remove self from scoped List on dismount
      setSearchAtomOnList((prev) => prev.filter((id) => id !== uuid));
      // Remove Self from map on dismount
      tableSearchAtomFamily.remove({ id: uuid });
    };
  }, [setSearchAtomOnList, uuid]);

  // Whenever new and incoming search changes, update the search object
  // Also, it is important for us to also update the atom value when our atomMap changes
  useEffect(() => {
    /** Gets the appropriate formatter based on the search type  */
    const formatter = customFormatter ?? getFormatter(serializer);
    setAtomValue({
      search: searchObject,
      reset,
      id: uuid,
      formatter,
      fields,
      label,
      showInParams,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on actual search change and also when our atom
  }, [searchObject, stableAtomFromMap]);

  // Whenever the provided search object changes, update the local search object
  useEffect(() => {
    const fields = fieldsRef.current;
    const serializer = serializerRef.current;
    const tempSearch = {};
    if (!search || !serializer) return setSearchObject({ searches: tempSearch });

    /** Gets the appropriate serializer based on the search type  */
    const formattedSerializer = getSerializer(serializer);

    fields?.forEach((field) =>
      Object.assign(tempSearch, { [field]: formattedSerializer(search, searchBy) })
    );
    setSearchObject({ searches: tempSearch });
  }, [search, searchBy]);

  return {
    searchObject,
    setSearchObject,
  };
};

/**
 *  Enables overall table state alignment for our data consumers by taking in
 *  table sorting state and then returning the appropriate search object to be used by the table
 */
const useTableSort = <TData extends RowData>(
  {
    store,
    sorting,
    getAllColumns,
  }: {
    store: store;
    sorting: SortingState;
    getAllColumns: ReturnType<typeof useReactTable<TData>>['getAllColumns'];
  },
  /** Dependencies to be used in the useMemo hook to ensure the sorting is updated when the dependencies change
   *
   *
   * Should be passed in as a useMemo dependency
   *
   *
   * eg: useTableSort({ store, sorting, getAllColumns }, useMemo(() => [_columns, sorting], [_columns, sorting]))
   */ dependencies: unknown[] = []
) => {
  const sortedColumn = useMemo(() => {
    if (sorting.length === 0) return;
    const sortedId = sorting?.[0]?.id;
    const sortedDirection = sorting?.[0]?.desc ? 'desc' : 'asc';

    const foundColumn = getAllColumns().find(
      (column) => column.id === sortedId || column.columnDef?.meta?.sortKey === sortedId
    );

    if (!foundColumn) return;

    return {
      sortKey: foundColumn.columnDef?.meta?.sortKey ?? foundColumn?.id,
      direction: sortedDirection,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting, getAllColumns, dependencies]);

  const searchObject = useMemo(() => {
    if (!sortedColumn) return { order: undefined };
    const sortKey = sortedColumn?.sortKey;

    const direction = sortedColumn.direction;

    return { order: { field: sortKey, direction } };
  }, [sortedColumn]);

  const { setSearchObject } = useTableSearch({
    store: store,
  });

  useEffect(() => {
    setSearchObject(searchObject);
  }, [searchObject, setSearchObject]);

  return {};
};

export { SERIALIZERS_DICT, useTableSearch, useTableSort };
